import { Chat } from '@matap/shared';
import { useEffect, useState, useCallback } from 'react';
import { FileAsset, FileAssetInfo } from '../file-manager/file.asset';
import { EventEmitter } from '@/webrtc/utils/event.emitter';

export const chatVisibilityEmitter = new EventEmitter<'change'>();

export function useFileAsset(chat: Chat, temporary: boolean) {
  const [visible, setVisible] = useState(false);
  const [localAsset, setLocalAsset] = useState<FileAssetInfo | undefined>();

  // 1. Initial Fetch / Creation (Replaces useIsDisposed)
  useEffect(() => {
    let isActive = true; // ✅ Perfect local scope prevents zombie callbacks

    if (chat.localFile) {
      setLocalAsset(chat.localFile.info());
      return;
    }

    if (chat.file) {
      FileAsset.create(chat.file, temporary).then((res) => {
        if (!isActive) {
          res.release('component unmounted before setting');
          return;
        }
        setLocalAsset((prev) => {
          if (prev?.source === res) return prev;
          return res.info();
        });
      });
    } else {
      setLocalAsset(undefined);
    }

    return () => {
      isActive = false; // ✅ Natively kills the async callback on unmount
    };
  }, [chat.file, chat.localFile, temporary]);

  // 2. Listeners (No disposedRef needed; cleanup handles unmount automatically)
  const updateListener = useCallback((s: FileAssetInfo) => setLocalAsset(s), []);
  const evictionListener = useCallback(() => setLocalAsset(undefined), []);
  const errorListener = useCallback((error: any, asset: any) => {
    console.error(error);
    console.log('debug', asset);
  }, []);

  useEffect(() => {
    if (localAsset?.source) {
      localAsset.source.on('state-change', updateListener);
      localAsset.source.on('evicted', evictionListener);
      localAsset.source.on('error', errorListener);
      return () => {
        // ✅ Synchronously removes listeners the millisecond the component dies
        localAsset.source.off('state-change', updateListener);
        localAsset.source.off('evicted', evictionListener);
        localAsset.source.off('error', errorListener);
      };
    }
  }, [localAsset?.source, updateListener, evictionListener, errorListener]);

  // 3. Memory Management
  useEffect(() => {
    return () => {
      if (localAsset?.source) {
        localAsset.source.release('component unmounted');
      }
    };
  }, [localAsset?.source]);

  // 4. Download Trigger
  useEffect(() => {
    if (localAsset?.source && !localAsset.source.isReleased() && visible) {
      updateListener(localAsset.source.info());
      localAsset.source.get(true);
    }
  }, [localAsset?.source, updateListener, visible]);

  // 5. Visibility Emitter
  useEffect(() => {
    const listener = (id: string, isVisible: boolean) => {
      if (chat._id === id) {
        setVisible(isVisible);
      }
    };
    chatVisibilityEmitter.on('change', listener);
    return () => {
      chatVisibilityEmitter.off('change', listener);
    };
  }, [chat._id]);

  return {
    state: localAsset?.source.isReleased() ? undefined : localAsset, //fix for React 18's strict mode fast remount on second initial render
    mediaMetaData: localAsset?.source?.mediaMetaData || chat.localFile?.mediaMetaData || chat.file?.metadata?.mediaMetaData,
  };
}
